'use client';

import { Box, Card, CardContent, Typography, Chip, Stack } from '@mui/material';
import { Calendar, DollarSign, Clock } from 'lucide-react';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';

interface Request {
    id: string;
    title: string;
    company: string;
    status: 'pending' | 'accepted' | 'rejected';
    budget: string;
    deadline: string;
    description: string;
    appliedDate: string;
}

interface RequestsListProps {
    requests: Request[];
}

export function RequestsList({ requests }: RequestsListProps) {
    const { themeStyle } = useThemeContext();
    const cardBg = theme[themeStyle].secondaryBg;
    const textColor = theme[themeStyle].tag_white;

    const getStatusChipProps = (status: string) => {
        switch (status) {
            case 'accepted':
                return {
                    label: `✓ Accepted`,
                    sx: { backgroundColor: '#dcfce7', color: '#15803d' },
                };
            case 'rejected':
                return {
                    label: `✗ Rejected`,
                    sx: { backgroundColor: '#fee2e2', color: '#b91c1c' },
                };
            case 'pending':
                return {
                    label: `⏳ Pending`,
                    sx: { backgroundColor: '#fef9c3', color: '#92400e' },
                };
            default:
                return {
                    label: `? Unknown`,
                    sx: { backgroundColor: '#f3f4f6', color: '#374151' },
                };
        }
    };

    return (
        <Stack spacing={2}>
            {requests.length === 0 ? (
                <Card sx={{ backgroundColor: cardBg }}>
                    <CardContent sx={{ p: 5, textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ color: textColor }}>
                            No requests found
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                requests.map(request => (
                    <Card key={request.id} sx={{ backgroundColor: cardBg }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box flex={1}>
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <Typography variant="h6" fontWeight={600} sx={{ color: textColor }}>
                                            {request.title}
                                        </Typography>
                                        <Chip size="small" {...getStatusChipProps(request.status)} />
                                    </Box>

                                    <Typography variant="body2" sx={{ color: textColor }} mb={0.5}>
                                        {request.company}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: textColor }} mb={2}>
                                        {request.description}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={3}
                                        alignItems="center"
                                        fontSize="0.875rem"
                                        sx={{ color: textColor }}>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <DollarSign size={16} style={{ color: textColor }} />
                                            <span>{request.budget}</span>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Calendar size={16} style={{ color: textColor }} />
                                            <span>{request.deadline}</span>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Clock size={16} style={{ color: textColor }} />
                                            <span>Applied {request.appliedDate}</span>
                                        </Box>
                                    </Stack>
                                </Box>

                                {request.status === 'pending' && (
                                    <Chip
                                        label="Awaiting Response"
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            color: '#b45309',
                                            borderColor: '#facc15',
                                            ml: 2,
                                            mt: 0.5,
                                            backgroundColor: cardBg,
                                        }}
                                    />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                ))
            )}
        </Stack>
    );
}
